import { MenonColor } from '@/core/models/menon-color'
import type { Member } from '@/core/models/member/member'
import { SELF_PERSPECTIVE, SELF_PERSPECTIVE_KEY } from '@/core/models/perspective'
import { ScaleType, type Scale } from '@/core/models/scale'
import type { Skill } from '@/core/models/skill'
import type {
  DistributedPerspectiveAnalysis,
  QuestionAnalysis,
  ScoredPerspectiveAnalysis,
  SkillAnalysis,
} from '@/core/models/surveys-analytics'
import { FeedbackType } from '@/core/models/feedback-type'
import { colorForUeberfachlicheKompetenz } from '../models/ueberfachliche-kompetenz-color'
import type { LessonScale } from '../models/teacher-assessment'
import {
  TEACHER_PERSPECTIVE,
  type KompetenzPerspective,
  type Kompetenzprofil,
  type KompetenzprofilAnalysis,
  type IndikatorPerspective,
  type MemberViewModel,
} from '../models/kompetenzprofil'

const PERSPECTIVE_ORDER = [SELF_PERSPECTIVE_KEY, TEACHER_PERSPECTIVE.key]

const sortPerspectives = <T extends { readonly key: string }>(perspectives: ReadonlyArray<T>) => {
  return [...perspectives].sort(
    (left, right) => PERSPECTIVE_ORDER.indexOf(left.key) - PERSPECTIVE_ORDER.indexOf(right.key),
  )
}

export const asMember = (member: MemberViewModel): Member => {
  const base = {
    id: member.id,
    email: member.email,
    canLogin: member.canLogin,
    lastUpdatedAvatar: member.lastUpdatedAvatar,
  }
  if (member.pending || member.firstName == null) {
    return { ...base, pending: true }
  }
  return {
    ...base,
    pending: false,
    firstName: member.firstName,
    lastName: member.lastName,
  }
}

const toScale = (scale: LessonScale): Scale => {
  return {
    type: ScaleType.Buttons,
    items: scale.items.map((item) => ({
      value: item.value,
      label: item.label,
    })),
  }
}

/** Histogram bars are the Lehrperson distribution, so that scale wins when both exist. */
export const chartScale = (profile: Kompetenzprofil): Scale | null => {
  const scale = profile.teacherScale ?? profile.selfScale
  if (!scale || scale.items.length === 0) {
    return null
  }
  return toScale(scale)
}

const perspectiveForKey = (key: string) => {
  if (key === SELF_PERSPECTIVE_KEY) {
    return SELF_PERSPECTIVE
  }
  return TEACHER_PERSPECTIVE
}

const toScoredPerspective = (perspective: IndikatorPerspective): ScoredPerspectiveAnalysis => {
  return {
    key: perspective.key,
    perspective: perspectiveForKey(perspective.key),
    numberOfFeedbacks: perspective.numberOfFeedbacks,
    numberOfSenders: perspective.numberOfSenders,
    average: perspective.average,
    feedbacks: perspective.feedbacks,
  }
}

const toDistributedPerspective = (
  perspective: KompetenzPerspective,
): DistributedPerspectiveAnalysis => {
  return {
    ...toScoredPerspective(perspective),
    distribution: perspective.distribution,
  }
}

const toSkill = (kompetenz: KompetenzprofilAnalysis['kompetenzen'][number]): Skill => {
  return {
    id: kompetenz.id,
    title: kompetenz.title,
    color: colorForUeberfachlicheKompetenz({ id: kompetenz.id }) ?? MenonColor.Bergseeli,
    parentSkill: {
      id: kompetenz.kompetenzbereich.key,
      title: kompetenz.kompetenzbereich.label,
      color: MenonColor.Bergseeli,
    },
  }
}

export const toSkillAnalyses = (analysis: KompetenzprofilAnalysis): SkillAnalysis[] => {
  return analysis.kompetenzen.map((kompetenz) => ({
    skill: toSkill(kompetenz),
    perspectives: sortPerspectives(kompetenz.perspectives).map(toDistributedPerspective),
  }))
}

export const toQuestionAnalyses = (analysis: KompetenzprofilAnalysis): QuestionAnalysis[] => {
  return analysis.kompetenzen.flatMap((kompetenz) =>
    kompetenz.kompetenzstufen.flatMap((stufe) =>
      stufe.indikatoren.map((indikator) => ({
        question: {
          id: indikator.id,
          content: indikator.text,
          feedbackType: FeedbackType.ScaleBased,
          skillIds: [kompetenz.id],
        },
        perspectives: sortPerspectives(indikator.perspectives).map(toScoredPerspective),
      })),
    ),
  )
}

/** One row per perspective. Counts are the highest seen on any Kompetenz, not a sum. */
export const toPerspectiveSummaries = (analysis: KompetenzprofilAnalysis) => {
  return PERSPECTIVE_ORDER.map((key) => {
    let numberOfFeedbacks = 0
    let numberOfSenders = 0
    let numberOfLessons = 0
    for (const kompetenz of analysis.kompetenzen) {
      const perspective = kompetenz.perspectives.find((entry) => entry.key === key)
      if (!perspective) {
        continue
      }
      numberOfFeedbacks = Math.max(numberOfFeedbacks, perspective.numberOfFeedbacks)
      numberOfSenders = Math.max(numberOfSenders, perspective.numberOfSenders)
      numberOfLessons = Math.max(numberOfLessons, perspective.numberOfLessons)
    }
    return { key, numberOfFeedbacks, numberOfSenders, numberOfLessons }
  })
}
